import { useCallback, useEffect, useState } from 'react';

function useLocalStorage(key, initialValue) {
  const readStoredValue = useCallback(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValue;
    }
  }, [initialValue, key]);

  const [storedValue, setStoredValue] = useState(readStoredValue);

  useEffect(() => {
    const syncStoredValue = (event) => {
      if (event.type === 'storage' && event.key !== key) {
        return;
      }
      if (event.type === 'local-storage-change' && event.detail !== key) {
        return;
      }
      setStoredValue(readStoredValue());
    };

    window.addEventListener('storage', syncStoredValue);
    window.addEventListener('local-storage-change', syncStoredValue);
    return () => {
      window.removeEventListener('storage', syncStoredValue);
      window.removeEventListener('local-storage-change', syncStoredValue);
    };
  }, [key, readStoredValue]);

  const setValue = useCallback(
    (value) => {
      try {
        // Use storage rather than this hook instance's render-time state so independent
        // consumers of the same key cannot overwrite one another with stale snapshots.
        const newValue = value instanceof Function ? value(readStoredValue()) : value;
        window.localStorage.setItem(key, JSON.stringify(newValue));
        setStoredValue(newValue);
        window.dispatchEvent(new CustomEvent('local-storage-change', { detail: key }));
      } catch (error) {
        console.warn(`Error setting localStorage key “${key}”:`, error);
      }
    },
    [key, readStoredValue],
  );

  const clearValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
      window.dispatchEvent(new CustomEvent('local-storage-change', { detail: key }));
    } catch (error) {
      console.warn(`Error clearing localStorage key “${key}”:`, error);
    }
  }, [initialValue, key]);

  return [storedValue, setValue, clearValue];
}

export default useLocalStorage;
